import { useCallback } from "react";

import { Box, Grid, SelectChangeEvent } from "@mui/material";
import { AvailablePaymentCurrency, formatCurrencyAmount, getCurrencySymbol, getDollarValue } from "@simplyk/common";
import { FieldValues } from "react-hook-form";

import { TipSuggestionObject } from "../../../gql/gql-types";
import { SelectedTip } from "../../PaymentProcessor/hooks/useComputeTip";
import { FormTextField } from "../FormTextField/FormTextField";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { FormSelectField, FormSelectFieldProps, FormSelectItem } from "@/forms-design-system/FormSelectField";
import { useTranslate } from "@/hooks/useTranslate";

export type FormPaymentTipSelectProps = Omit<FormSelectFieldProps<FieldValues>, "fullWidth" | "value" | "onChange"> & {
  currency: AvailablePaymentCurrency;
  options: [TipSuggestionObject, TipSuggestionObject, TipSuggestionObject] | undefined;
  value: SelectedTip;
  onChange: (event: SelectChangeEvent<unknown>) => void;
  onChangeFreeTips: (event: React.ChangeEvent<HTMLInputElement>) => void;
  freeTips: number | "";
  onBlurTip: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  freeTipInputRef?: React.RefObject<HTMLInputElement | null>;
  isAuction: boolean;
  isSuggestedTipsAmount: boolean;
};

export const FormPaymentTipSelect = ({
  currency,
  options,
  defaultValue,
  value,
  onChange,
  onChangeFreeTips,
  freeTips,
  onBlurTip,
  freeTipInputRef,
  isAuction,
  isSuggestedTipsAmount,
  ...rest
}: FormPaymentTipSelectProps) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();

  const renderOptionLabel = useCallback(
    (suggestedTip: TipSuggestionObject) => {
      if (suggestedTip.amount === 0 && currency) {
        return formatCurrencyAmount({ value: 0, locale: isoLocale, currency });
      }

      if ((!suggestedTip.percentage || isSuggestedTipsAmount) && currency && suggestedTip.amount !== 0) {
        return formatCurrencyAmount({
          value: getDollarValue(suggestedTip.amount),
          locale: isoLocale,
          currency,
        });
      }

      if (suggestedTip.percentage && currency && suggestedTip.amount !== 0 && !isSuggestedTipsAmount) {
        return `${suggestedTip.percentage.toFixed(2)}%${
          isAuction
            ? ""
            : ` (${formatCurrencyAmount({ value: getDollarValue(suggestedTip.amount), locale: isoLocale, currency })})`
        }`;
      }

      return "";
    },
    [currency, isAuction, isSuggestedTipsAmount, isoLocale]
  );

  return (
    <Grid container id="tip-section-input">
      <Grid item xs={12}>
        <FormSelectField
          defaultValue={defaultValue}
          value={value.isFree ? "free" : value.percentage?.toString()}
          data-test="tip-menu-item-select"
          onChange={onChange}
          renderValue={(value) => {
            if (value === "free") {
              return t("donationForm", "freeTips");
            }

            const selectedTip = options?.find((tip) => tip.percentage?.toString() === value);
            if (!selectedTip) {
              return value;
            }
            return renderOptionLabel(selectedTip);
          }}
          fullWidth
          {...rest}
        >
          {options?.map((o) => (
            <FormSelectItem key={o.percentage} value={o.percentage} data-test="tip-menu-item">
              {renderOptionLabel(o)}
            </FormSelectItem>
          ))}
          <FormSelectItem key="free" value="free" data-test="free-tips-menu-item">
            {t("donationForm", "freeTips")}
          </FormSelectItem>
        </FormSelectField>

        {value.isFree && (
          <Box pt={2}>
            <FormTextField
              value={freeTips}
              label={t("donationForm", "tip")}
              type="number"
              onChange={onChangeFreeTips}
              onBlur={onBlurTip}
              endAdornment={getCurrencySymbol(isoLocale, currency)}
              inputProps={{
                type: "number",
                inputMode: "decimal",
                onWheel: (event: React.WheelEvent<HTMLInputElement>) => {
                  event.currentTarget.blur();
                },
              }}
              error={freeTips < 0}
              helperText={freeTips < 0 ? t("donationForm", "tipsIsGreaterThanZero") : undefined}
              inputRef={freeTipInputRef}
              data-test="manual-contribution-input"
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );
};
